/**
 * §7.4 稽核軌跡。與 mockup 的差異：**加分頁、時間範圍、操作人篩選**。
 *
 * §12.2 全公司層級的變更（scope = 'global'）在每個廠區都看得到，並標示【全公司】——
 * 否則高雄廠的主管不會知道料號字典被改過，即使那改動了他畫面上的名稱。
 *
 * §6.1 `settings.measurement.write` 的變更要「在稽核軌跡以警示色標示」。
 */

import { useState } from 'react'
import { download, withQuery } from '../api/client.js'
import { useQuery } from '../api/hooks.js'
import { Button, Card, Empty, Mono, Pill, Table, Td, TextInput, Select } from '../components/ui.js'
import { ts } from '../../../shared/lib/format.js'
import { EVENT_LABEL } from '../../../shared/types/events.js'

interface AuditRow {
  seq: string
  event_id: string
  type: string
  typeLabel: string
  site_id: string
  scope: 'site' | 'global'
  isGlobal: boolean
  actor_display_name: string | null
  occurred_at: string
  payload: Record<string, unknown>
}

export function AuditLog() {
  const [type, setType] = useState('')
  const [from, setFrom] = useState('')
  const [globalOnly, setGlobalOnly] = useState(false)
  const [pages, setPages] = useState<AuditRow[][]>([])
  const [cursor, setCursor] = useState<string | null>(null)

  const params = {
    type, globalOnly: globalOnly || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    cursor, limit: 50,
  }
  const { data, error, loading } = useQuery<{ items: AuditRow[]; nextCursor: string | null }>(
    withQuery('/api/audit', params),
  )
  const items = [...pages.flat(), ...(data?.items ?? [])]

  const resetPaging = (fn: () => void) => { setPages([]); setCursor(null); fn() }

  return (
    <Card
      title="稽核軌跡"
      subtitle="領域事件。只新增、不修改——任何修正都是一筆新的沖正事件"
      right={
        <Button size="sm" onClick={() => download('/api/audit', { ...params, cursor: undefined, format: 'csv' })}>
          下載 CSV
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Select value={type} onChange={(e) => resetPaging(() => setType(e.target.value))}>
          <option value="">全部事件</option>
          {Object.entries(EVENT_LABEL).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </Select>
        <TextInput
          type="date"
          value={from}
          onChange={(e) => resetPaging(() => setFrom(e.target.value))}
          className="max-w-[160px]"
        />
        <label className="flex items-center gap-1.5 text-sm text-ink-700 min-h-touch">
          <input
            type="checkbox"
            checked={globalOnly}
            onChange={(e) => resetPaging(() => setGlobalOnly(e.target.checked))}
          />
          只看全公司層級的變更
        </label>
      </div>

      {error && <p className="text-sm text-red-700">{error.message}</p>}
      {items.length === 0 && !loading && <Empty>這個範圍內沒有事件。</Empty>}

      {items.length > 0 && (
        <Table head={['序號', '時間', '事件', '範圍', '操作人', '內容']} dense>
          {items.map((e) => {
            // §6.1：量測點的變更以警示色標示——它會改變後續所有工單的資料品質
            const highRisk = e.type === 'ConfigChanged' && e.payload.highRisk === true
            return (
              <tr key={e.event_id} className={highRisk ? 'bg-amber-50' : ''}>
                <Td className="tabular-nums text-xs text-ink-400">{e.seq}</Td>
                <Td className="text-xs whitespace-nowrap">{ts(e.occurred_at)}</Td>
                <Td className="text-xs">
                  {e.typeLabel}
                  {highRisk && <Pill tone="amber">高風險</Pill>}
                </Td>
                <Td className="text-xs">
                  {e.isGlobal
                    ? <Pill tone="violet" title="全公司層級的變更，每個廠區都受影響">全公司</Pill>
                    : <span className="text-ink-400">{e.site_id}</span>}
                </Td>
                <Td className="text-xs">{e.actor_display_name ?? '—'}</Td>
                <Td className="text-xs text-ink-600 max-w-[380px]">
                  <Summary type={e.type} payload={e.payload} />
                </Td>
              </tr>
            )
          })}
        </Table>
      )}

      {data?.nextCursor && (
        <div className="mt-3 text-center">
          <Button
            size="sm"
            disabled={loading}
            onClick={() => { setPages((p) => [...p, data.items]); setCursor(data.nextCursor) }}
          >{loading ? '載入中…' : '載入更多'}</Button>
        </div>
      )}
    </Card>
  )
}

/** 事件內容的一行摘要。刻意不倒整包 JSON——那個看不出重點。 */
function Summary({ type, payload }: { type: string; payload: Record<string, unknown> }) {
  const p = payload as Record<string, any>
  switch (type) {
    case 'ConfigChanged': {
      // 型號基準的變更帶 modelChange，講得出「誰對哪一個型號做了什麼」，
      // 比「設定項目 modelSpecs」有用得多
      const mc = p.modelChange as
        | { action: string; modelCode: string; status?: string; changedFields?: string[]
            proposedBy?: string | null; usage?: { panels: number; workOrders: number } }
        | undefined
      if (mc) {
        const verb = mc.action === 'create'
          ? (mc.status === 'provisional' ? '申請暫定型號' : '新增型號')
          : mc.action === 'confirm' ? '覆核型號' : '編輯型號'
        return <>
          {verb} <Mono>{mc.modelCode}</Mono>
          {mc.action === 'confirm' && mc.proposedBy ? ` · 由 ${mc.proposedBy} 申請` : ''}
          {mc.changedFields?.length ? ` · 改了 ${mc.changedFields.join('、')}` : ''}
          {mc.usage && (mc.usage.panels > 0 || mc.usage.workOrders > 0)
            ? ` · 已用於 ${mc.usage.panels} 片 / ${mc.usage.workOrders} 張工單` : ''}
          {p.note ? ` · ${p.note}` : ''}
        </>
      }
      return <>設定項目 <Mono>{p.key ?? p.path}</Mono>{p.note ? ` · ${p.note}` : ''}</>
    }
    case 'WorkOrderOpened':
      return <>工單 <Mono>{p.workOrder?.workOrderId}</Mono> · {p.workOrder?.resourceId} · 預計 {p.workOrder?.plannedCount} 片</>
    case 'WorkOrderClosed':
      return <>工單 <Mono>{p.workOrderId}</Mono></>
    case 'IssuePosted':
      return <>{p.lines?.length ?? 0} 行 · 解析度 {p.issueOrder?.resolutionGrade}
        {p.issueOrder?.approvedBy ? ` · 核可 ${p.issueOrder.approvedBy}` : ''}
        {p.weighEvents?.length ? ` · ${p.weighEvents.length} 筆量測` : ''}</>
    case 'ReceiptPosted':
      return <>{p.lines?.length ?? 0} 行
        {p.weighEvents?.length ? ` · ${p.weighEvents.length} 筆量測` : ''}
        {p.lines?.length === 0 ? '（補秤）' : ''}</>
    case 'ShipmentPosted':
      return <>出廠 <Mono>{p.shipment?.shipmentId}</Mono> · {p.shipment?.buyer} · {p.shipment?.vehicleNo}</>
    case 'PanelRegistered':
      return <><Mono>{p.panel?.panelId}</Mono> · {p.panel?.modelCode ?? '未辨識'}</>
    case 'DeliveryReceived':
      return <><Mono>{p.deliveryId}</Mono> · 申報 {p.declaredCount} 片 · {p.customer}</>
    case 'IdBatchGenerated':
      return <>{p.idType === 'panel' ? 'PV 號' : '料件號'} {p.ids?.length ?? 0} 組</>
    case 'IdBatchReserved':
      return <>預提 {p.ids?.length ?? 0} 組 · <Mono>{p.reservationId}</Mono></>
    case 'AdjustmentPosted':
      return <>沖正 <Mono>{p.targetLedgerId}</Mono> · {p.qtyKg} kg · {p.reason}</>
    case 'StockCounted':
      return <>料件 <Mono>{p.itemId}</Mono> · 實盤 {p.countedKg} kg</>
    case 'PanelScrapped':
    case 'PanelDetached':
      return <><Mono>{p.panelId}</Mono> · {p.reason}</>
    default:
      return <span className="text-ink-400">—</span>
  }
}
